'use client'

import { useState } from 'react'
import { Heart, Menu, Usb, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { VisitorCounter } from '@/components/visitor-counter'
import { AuthBadge } from '@/components/auth-badge'
import { NotificationBell } from '@/components/notification-bell'

const navLinks = [
  { label: 'Features', href: '/#features' },
  { label: 'Radios', href: '/#radios' },
  { label: 'Get Started', href: '/#get-started' },
  { label: 'Mobile', href: '/beginners-guide#mobile' },
  { label: 'Docs', href: '/docs' },
  { label: 'Forums', href: '/forum' },
]

export function SiteHeader() {
  const [open, setOpen] = useState(false)

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-[96rem] items-center justify-between gap-1 px-3 md:gap-2 md:px-2 lg:gap-3 lg:px-4">
        <div className="flex shrink-0 items-center gap-3">
          <a href="/" className="flex shrink-0 items-center gap-2 whitespace-nowrap">
            <img
              src="/newchirp-logo.svg"
              alt="NewChirp logo"
              className="h-9 w-9 rounded-lg"
              width={36}
              height={36}
            />
            <span className="font-mono text-lg font-bold tracking-tight md:max-[899px]:hidden">
              New<span className="text-primary">Chirp</span>
            </span>
          </a>
        </div>

        <nav className="hidden min-w-0 items-center gap-1 md:flex lg:gap-3 xl:gap-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="whitespace-nowrap text-xs text-muted-foreground transition-colors hover:text-foreground lg:text-sm"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden shrink-0 items-center gap-0.5 md:flex lg:gap-1 xl:gap-1.5">
          <NotificationBell />
          <Button
            className="web-programmer-halo"
            variant="secondary"
            size="sm"
            nativeButton={false}
            render={<a href="/programmer" aria-label="Open the Web Programmer beta" />}
          >
            <Usb className="h-3.5 w-3.5" />
            <span className="hidden md:inline lg:hidden">Programmer</span>
            <span className="hidden lg:inline">Web Programmer</span>
          </Button>
          <Button
            className="hidden xl:inline-flex"
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<a href="/register" />}
          >
            Register
          </Button>
          <Button
            className="hidden xl:inline-flex"
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<a href="/donate" aria-label="Donate to the NewChirp project" />}
          >
            <Heart className="h-3.5 w-3.5 text-red-500" />
            Donate
          </Button>
          <Button size="sm" nativeButton={false} render={<a href="/#download" />}>
            Download
          </Button>
          <span aria-hidden className="mx-1 h-6 w-px bg-border" />
          <AuthBadge />
        </div>

        <div className="flex items-center gap-1 md:hidden">
          <Button
            className="web-programmer-halo"
            variant="secondary"
            size="sm"
            nativeButton={false}
            render={<a href="/programmer" aria-label="Open the Web Programmer beta" />}
          >
            <Usb className="h-3.5 w-3.5" />
            <span className="hidden min-[768px]:inline lg:hidden">Programmer</span>
            <span className="hidden lg:inline">Web Programmer</span>
          </Button>
          <NotificationBell />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md text-foreground"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <div className="border-t border-border/50 bg-background/65">
        <div className="mx-auto flex min-h-9 min-w-0 max-w-[96rem] items-center overflow-hidden px-4 py-1.5">
          <VisitorCounter />
        </div>
      </div>

      {open && (
        <div className="border-t border-border/60 bg-background md:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {[...navLinks, { label: 'Community', href: '/community' }].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
            <Button
              className="web-programmer-halo"
              variant="secondary"
              nativeButton={false}
              render={
                <a href="/programmer" onClick={() => setOpen(false)} />
              }
            >
              <Usb className="h-4 w-4" />
              Web Programmer Beta
            </Button>
            <Button
              className="mt-2"
              variant="outline"
              nativeButton={false}
              render={<a href="/register" onClick={() => setOpen(false)} />}
            >
              Register
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={
                <a
                  href="/donate"
                  aria-label="Donate to the NewChirp project"
                  onClick={() => setOpen(false)}
                />
              }
            >
              <Heart className="h-4 w-4 text-red-500" />
              Donate
            </Button>
            <Button
              nativeButton={false}
              render={<a href="/#download" onClick={() => setOpen(false)} />}
            >
              Download NewChirp
            </Button>
            <AuthBadge mobile />
          </nav>
        </div>
      )}
    </header>
  )
}
